import React, { createRef, PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { IDT } from '@ff-os/pdp-shared-redux/IDT';
import { ButtonWithIcon } from '@ff-os/shared-react/components/buttons/button-with-icon/ButtonWithIcon';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { Heading } from '@ff-os/shared-react/components/panel/Heading';
import { Panel } from '@ff-os/shared-react/components/panel/Panel';
import { PriceLabel } from '@ff-os/shared-react/components/price-label/PriceLabel';
import { useExternalFromPriceRender } from '@ff-os/shared-react/hooks/useExternalFromPriceRender';
import { useScrollIntoView } from '@ff-os/shared-react/hooks/useScrollIntoView';
import { selectFailedUploadsCount, selectHasVariantUpdated, selectIsNewGroup, selectSuccessfulUploadsCount } from '@ff-scene-plugins/photo-prints-redux/slices/groupsUiSlice';
import { selectPhotoIdsByGroupId } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import classNames from 'classnames';
import { selectActiveVariant, selectCanRemoveGroup, selectIsEmptyGroup } from '../../../_redux-groups';
import { createFaceDetectionWebWorker } from '../../../helpers/loadFaceDetectionWebWorker';
import { removeGroup } from '../../../thunks/removeGroup';
import { VariantOptionGroup } from './variant-option-group/VariantOptionGroup';
import styles from './PhotoPrintGroupPanel.module.scss';

const _PhotoPrintGroupPanel = ({
    isEmpty, groupId, activeVariant,
    successfulUploadsCount, failedUploadsCount,
    isNewGroup, hasVariantUpdated,
    browse, remove, canRemove, children, dragging
}: Props) => {

    const { t } = useTranslation();

    const isSuccessMessage = successfulUploadsCount > 0 || isNewGroup || hasVariantUpdated;
    const isErrorMessage = failedUploadsCount > 0;
    const hasMessage = isSuccessMessage || isErrorMessage;

    const panelClassName = classNames(styles.panel, {
        [styles.isEmpty]: isEmpty,
        [styles.success]: !dragging && isSuccessMessage,
        [styles.error]: !dragging && isErrorMessage
    });

    const ref = createRef<HTMLDivElement>();
    const scrollIntoView = useScrollIntoView(ref, { includeHidden: true });

    useEffect(() => {

        if (hasMessage) {
            scrollIntoView();
        }

        createFaceDetectionWebWorker().catch(console.error);

    }, [hasMessage, scrollIntoView]);

    return (
        <Panel className={panelClassName} ref={ref}>
            <Grid direction='column' gap='s' fullHeight fullWidth>
                <Cell className={styles.cell}>
                    <Heading className={styles.heading} icon='shopping-cart'>
                        {t('photo_prints.order_as')}
                        <RequestPriceLabel priceRequest={{sku : activeVariant.sku}} />
                    </Heading>
                </Cell>

                <Cell className={styles.cell}>
                    <VariantOptionGroup activeVariant={activeVariant} groupId={groupId} />
                </Cell>

                <Cell className={styles.photosCell}>
                    {children}
                </Cell>

                {hasMessage ? (
                    <Cell className={classNames(styles.messagesCell, styles.cell, styles.message)}>
                        <ul className={styles.messages}>
                            {hasVariantUpdated ? (
                                <li>{t('photo_prints.variant_changed')}</li>
                            ) : (
                                <>
                                    {failedUploadsCount > 0 && (
                                        <li>{t('photo_prints.photos_errored', { count: failedUploadsCount })}</li>
                                    )}
                                    {successfulUploadsCount > 0 && (
                                        <li>{t('photo_prints.photos_uploaded', { count: successfulUploadsCount })}</li>
                                    )}
                                    {isNewGroup && (
                                        <li>{t('photo_prints.group_added')}</li>
                                    )}
                                </>
                            )}
                        </ul>
                    </Cell>
                ) : (
                    <Cell className={classNames(styles.cell, styles.message)}>
                        {!isEmpty && (
                            <ButtonWithIcon strokeWidth={2} style='primary' onClick={browse}
                                icon='alert-circle-plus' className={styles.uploadButton}>
                                {t('photo_prints.add_more')}
                            </ButtonWithIcon>
                        )}

                        {canRemove && (
                            <ButtonWithIcon strokeWidth={2} style='depressed' onClick={remove}
                                icon='general-ui-bin' className={styles.removeButton}>
                                {t('photo_prints.remove_group')}
                            </ButtonWithIcon>
                        )}
                    </Cell>
                )}
            </Grid>
        </Panel>
    );
};

const RequestPriceLabel = ({ priceRequest }: { priceRequest: IDT.FromPriceRequest }) => {

    const { t, i18n } = useTranslation();
    const { destinationRef, plainText, object } = useExternalFromPriceRender(priceRequest);
    const priceText = object
        ? object.price.toLocaleString(i18n.language, { currency: object.currency, style: 'currency' })
        : '';

    return (
        <span style={{marginLeft: 'auto'}} ref={destinationRef}>
            {plainText && <PriceLabel className={styles.label}>{plainText} {t('photo_prints.price_apiece')}</PriceLabel>}
            {object && <PriceLabel className={styles.label}>{priceText} {t('photo_prints.price_apiece')}</PriceLabel>}
        </span>
    );
};

type OwnProps = PropsWithChildren<{
    groupId: string;
    browse: () => void;
    dragging: boolean;
}>;

type Props = OwnProps & {
    isEmpty: boolean;
    canRemove: boolean;
    activeVariant: PhotoPrintsJson.Variant;
    remove: () => void;
    successfulUploadsCount: number;
    failedUploadsCount: number;
    isNewGroup: boolean;
    hasVariantUpdated: boolean;
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => ({
    isEmpty: selectIsEmptyGroup(state, ownProps.groupId),
    photoIds: selectPhotoIdsByGroupId(state, ownProps.groupId),
    activeVariant: selectActiveVariant(state, ownProps.groupId),
    canRemove: selectCanRemoveGroup(state, ownProps.groupId),
    successfulUploadsCount: selectSuccessfulUploadsCount(state, ownProps.groupId),
    failedUploadsCount: selectFailedUploadsCount(state, ownProps.groupId),
    isNewGroup: selectIsNewGroup(state, ownProps.groupId),
    hasVariantUpdated: selectHasVariantUpdated(state, ownProps.groupId)
});

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: OwnProps) => ({
    remove: () => dispatch(removeGroup(ownProps.groupId))
});

export const PhotoPrintGroupPanel = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PhotoPrintGroupPanel);